// The theme types for MuiDateCalendar and the tree views, without a runtime import.
/// <reference types="@mui/x-date-pickers/themeAugmentation" />
/// <reference types="@mui/x-tree-view/themeAugmentation" />
import { LanguageEnum } from '@luminovo/commons';
import {
    createTheme as muiCreateTheme,
    StepIcon,
    stepIconClasses,
    type StepIconProps,
    Theme,
    useTheme,
} from '@mui/material';
// oxlint-disable-next-line no-restricted-imports -- the barrel doesn't export the locales
import {
    csCZ,
    deDE,
    enUS,
    esES,
    fiFI,
    frFR,
    hiIN,
    huHU,
    itIT,
    jaJP,
    nlNL,
    plPL,
    ptPT,
    svSE,
    thTH,
    trTR,
    zhCN,
} from '@mui/material/locale';
import {
    ArrowDownIcon,
    ChevronDownIcon,
    ChevronFirstIcon,
    ChevronLastIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EllipsisIcon,
    type LucideIcon,
    type LucideProps,
    PlusIcon,
    StarIcon,
    UserRoundIcon,
    XIcon,
} from 'lucide-react';
import { createElement, type ReactElement, type SVGProps } from 'react';

import { colorSystem } from './colorSystem';
import { CircleXFilledIcon } from './components/icons/filledIcons';
import { ICON_SVG } from './components/icons/iconMarker';
import {
    CheckboxCheckedIcon,
    CheckboxEmptyIcon,
    CheckboxIndeterminateIcon,
    RadioCheckedIcon,
    RadioEmptyIcon,
} from './components/icons/selectionControlIcons';
import { STATUS_ICONS } from './components/StatusIcon/statusIcons';

export const INTER = "'Inter var', 'Inter', system-ui, sans-serif";

export const NAVBAR_HEIGHT = '48px';
export const MAX_WIDTH = '90rem';

export { colorSystem };

export const colorVariables = {
    border: {
        main: colorSystem.neutral[3],
    },
    text: {
        main: colorSystem.neutral[7],
        secondary: colorSystem.neutral[6],
        title: colorSystem.neutral[8],
    },
};

export type Colors = keyof typeof colorSystem;

export const h1 = {
    fontFamily: INTER,
    fontSize: '24px',
    lineHeight: '28px',
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 32',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 550,
};

export const h2 = {
    fontFamily: INTER,
    fontSize: '20px',
    lineHeight: '24px',
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 32',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 600,
};

export const h3 = {
    fontFamily: INTER,
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 32',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 600,
};

export const h4 = {
    fontFamily: INTER,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 32',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 600,
};
export const h5 = {
    fontFamily: INTER,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '-0.01em',
    fontVariationSettings: '"opsz" 32',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 600,
};
export const body = {
    fontFamily: INTER,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.01em',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 440,
};

export const bodySemiBold = {
    ...h4,
};

export const bodySmall = {
    fontFamily: INTER,
    fontSize: '12px',
    lineHeight: '16px',
    letterSpacing: '-0.01em',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 440,
};

export const bodySmallSemiBold = {
    ...h5,
};

export const caption = {
    fontFamily: INTER,
    fontSize: '10px',
    lineHeight: '12px',
    letterSpacing: '-0.01em',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1',
    fontWeight: 500,
};

export const code = {
    fontFamily: INTER,
    fontSize: '14px',
    lineHeight: '24px',
    fontFeatureSettings: '"ss04" 1, "cv01" 1, "cv03" 1, "cv04" 1, "cv06" 1, "zero" 1',
    fontWeight: 500,
};

export const focusedStyles = {
    border: `1px solid ${colorSystem.primary[5]}`,
    boxShadow: `0px 0px 0px 2px ${colorSystem.primary[3]}`,
    backgroundColor: colorSystem.neutral.white,
};
export const hoveredStyles = { border: `1px solid ${colorSystem.primary[4]}` };

export const disabledStyles = {
    backgroundColor: colorSystem.neutral[1],
    border: `1px solid ${colorSystem.neutral[3]}`,
};

/** The theme's focus ring for an element MUI draws none on, such as a plain `<button>`: a style for `styled`. */
export const focusVisibleRing = ({ theme }: { theme: Theme }) => ({
    '&:focus-visible': theme.focusVisible,
});

/** A Lucide icon at a fixed size, for an MUI slot that sizes its own icon with font-size or styles this doesn't get. */
function fixedSizeIcon(Icon: LucideIcon, size: LucideProps['size']) {
    // Some slots pass `fontSize` and `ownerState`, which a Lucide icon would write onto its svg.
    return ({ ownerState, fontSize, style, ...props }: LucideProps & { ownerState?: unknown }) =>
        // flexShrink also where no CssBaseline sets it: the Alert's close button would squeeze its 20px X to 12px.
        createElement(Icon, { size, style: { flexShrink: 0, ...style }, ...props });
}

/** The size of MUI's own Checkbox and Radio icons, from the control's size, which MUI hands the icon as `fontSize`. */
function selectionControlIconSize(fontSize: unknown): number {
    return fontSize === 'small' ? 20 : fontSize === 'large' ? 35 : 24;
}

/** Material draws an 18-unit box in a 24-unit viewBox, so the 20-unit drawing sits centred in a 20 × 24 / 18 one. */
const CHECKBOX_VIEWBOX = '-3.333 -3.333 26.667 26.667';

/** MuiCheckbox's default icon: the design-system drawing, with Material's box size in an icon of MUI's size. */
function checkboxIcon(Drawing: typeof CheckboxEmptyIcon) {
    const CheckboxIcon = ({ fontSize, ...props }: SVGProps<SVGSVGElement>) => {
        const size = selectionControlIconSize(fontSize);
        return createElement(Drawing, { width: size, height: size, viewBox: CHECKBOX_VIEWBOX, ...props });
    };
    return createElement(CheckboxIcon);
}

/** MuiRadio's default icon: the design-system drawing at MUI's icon size, so the layout around it stays. */
function radioIcon(Drawing: typeof RadioEmptyIcon) {
    const RadioIcon = ({ fontSize, ...props }: SVGProps<SVGSVGElement>) => {
        const size = selectionControlIconSize(fontSize);
        // Material's ring is 20/24 of a medium or large icon; a small radio's 20px icon draws the 20-unit ring as is.
        const box = fontSize === 'small' ? {} : { viewBox: '-2 -2 24 24' };
        return createElement(Drawing, { width: size, height: size, ...box, ...props });
    };
    return createElement(RadioIcon);
}

const TabsStartScrollIcon = fixedSizeIcon(ChevronLeftIcon, 20);
const TabsEndScrollIcon = fixedSizeIcon(ChevronRightIcon, 20);
const AlertCloseIcon = fixedSizeIcon(XIcon, 20);
// MUI's own fallback is 75% of the avatar.
const AvatarFallbackIcon = fixedSizeIcon(UserRoundIcon, '75%');
// The calendar's arrow buttons and the tree items draw their icons at 18px today.
const CalendarLeftArrowIcon = fixedSizeIcon(ChevronLeftIcon, 18);
const CalendarRightArrowIcon = fixedSizeIcon(ChevronRightIcon, 18);
const TreeExpandIcon = fixedSizeIcon(ChevronRightIcon, 18);
const TreeCollapseIcon = fixedSizeIcon(ChevronDownIcon, 18);
// TableSortLabel draws its arrow at its 18px font size and turns it for ascending.
const TableSortArrowIcon = fixedSizeIcon(ArrowDownIcon, 18);

/** MuiStepLabel's icon: status glyphs for a completed or failed step, at MUI's 24px; a numbered step stays MUI's. */
function StepLabelIcon({ ownerState, ...props }: StepIconProps & { ownerState?: unknown }): ReactElement {
    const theme = useTheme();
    const { active = false, completed = false, error = false, icon, className } = props;
    if ((typeof icon !== 'number' && typeof icon !== 'string') || !(completed || error)) {
        return createElement(StepIcon, props);
    }
    const stateClasses = [
        stepIconClasses.root,
        active && stepIconClasses.active,
        completed && stepIconClasses.completed,
        error && stepIconClasses.error,
    ];
    return createElement(error ? STATUS_ICONS.error.filled : STATUS_ICONS.success.filled, {
        size: 24,
        color: error ? theme.palette.error.main : theme.palette.primary.main,
        className: [className, ...stateClasses].filter(Boolean).join(' '),
    });
}

export const createTheme = (locale: LanguageEnum): Theme =>
    muiCreateTheme(
        {
            palette: {
                mode: 'light',
                primary: {
                    main: colorSystem.primary[6],
                    light: colorSystem.primary[2],
                    dark: colorSystem.primary[7],
                },
                secondary: {
                    main: colorSystem.primary[5],
                    light: colorSystem.primary[4],
                },
                error: {
                    main: colorSystem.red[6],
                    light: colorSystem.red[2],
                    dark: colorSystem.red[8],
                },
                warning: {
                    main: colorSystem.yellow[6],
                    light: colorSystem.yellow[2],
                    dark: colorSystem.yellow[8],
                },
                success: {
                    main: colorSystem.green[6],
                    light: colorSystem.green[2],
                    dark: colorSystem.green[8],
                },
                background: {
                    default: colorSystem.neutral.white,
                },
                action: {
                    // Opaque, unlike MUI's rgba(0, 0, 0, 0.54): Lucide's overlapping paths darken a translucent stroke.
                    active: colorSystem.neutral[7],
                    disabledBackground: colorSystem.neutral[5],
                    disabled: colorSystem.neutral[1],
                },
                text: {
                    primary: colorSystem.neutral[9],
                    secondary: colorSystem.neutral[8],
                    disabled: colorSystem.neutral[5],
                },
                grey: {
                    '100': colorSystem.neutral[1],
                    '200': colorSystem.neutral[2],
                    '300': colorSystem.neutral[3],
                    '400': colorSystem.neutral[4],
                    '500': colorSystem.neutral[5],
                    '600': colorSystem.neutral[6],
                    '700': colorSystem.neutral[7],
                    '800': colorSystem.neutral[8],
                    '900': colorSystem.neutral[9],
                },
            },
            typography: {
                fontFamily: INTER,
                h1: h1,
                h2: h2,
                h3: h3,
                h4: h4,
                h5: h5,
                subtitle1: bodySmall, // body small in Figma
                subtitle2: bodySmallSemiBold, // body small SemiBold in Figma
                body1: body, // body in Figma
                body2: bodySemiBold, // body SemiBold in Figma
                caption: caption,
                button: {
                    textTransform: 'none',
                },
            },
            // The keyboard's focus ring on MUI's buttons, tabs, menu items, links and checkboxes: 2px wide, 2px
            // outside the border; a click's focus draws none. Tabs, menu items and list items draw it inside their
            // border, which their scrolling parent would otherwise cut off. One color for all, the destructive buttons
            // too: the ring sits on the page, and red 5 has under 3:1 on white.
            focusVisible: {
                outlineColor: colorSystem.primary[5],
            },
            components: {
                // No ripples anywhere: a focus ripple would pulse under the ring on tabs, switches and icon buttons.
                MuiButtonBase: {
                    defaultProps: {
                        disableRipple: true,
                    },
                },
                MuiCssBaseline: {
                    styleOverrides: {
                        '*, *::before, *::after': {
                            boxSizing: 'border-box',
                        },
                        // Icons keep their size in a flex row, as MUI's do; `:where` lets a component's own rule win.
                        [`:where(${ICON_SVG})`]: { flexShrink: 0 },
                    },
                },
                MuiMenu: {
                    defaultProps: {
                        elevation: 1,
                    },
                },
                MuiFormControlLabel: {
                    styleOverrides: {
                        root: {
                            width: 'fit-content',
                        },
                    },
                },
                MuiTextField: {
                    defaultProps: {
                        variant: 'outlined',
                        size: 'small',
                    },
                    styleOverrides: {
                        root: {
                            '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button':
                                {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },

                            '& input[type=number]': {
                                MozAppearance: 'textfield',
                            },
                        },
                    },
                },
                MuiBadge: {
                    styleOverrides: {
                        badge: {
                            fontSize: '12px !important',
                            padding: '0 4px !important',
                            margin: '0 !important',
                            minWidth: '18px !important',
                            height: '18px !important',
                        },
                    },
                },
                MuiTooltip: {
                    defaultProps: {
                        arrow: true,
                        placement: 'bottom',
                    },
                    styleOverrides: {
                        tooltip: {
                            color: colorSystem.neutral[1],
                            backgroundColor: colorSystem.neutral[9],
                        },
                        arrow: {
                            color: colorSystem.neutral[9],
                        },
                    },
                },
                MuiButton: {
                    defaultProps: {
                        disableRipple: true,
                        size: 'large',
                    },
                    styleOverrides: {
                        text: {
                            padding: '6px 12px',
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            fontSize: '18px',
                            lineHeight: '20px',
                            padding: '11px',
                            // The disabled text color. MUI's is `action.disabled`, neutral 1, which is almost white.
                            '&.Mui-disabled': {
                                color: colorSystem.neutral[5],
                            },
                        },
                        sizeSmall: {
                            height: '32px',
                            width: '32px',
                            fontSize: '16px',
                            padding: '10px',
                        },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paperWidthSm: {
                            maxWidth: '44rem',
                        },
                    },
                },
                MuiDialogActions: {
                    styleOverrides: {
                        root: {
                            padding: '12px 24px',
                        },
                    },
                },
                MuiCheckbox: {
                    defaultProps: {
                        disableRipple: true,
                        icon: checkboxIcon(CheckboxEmptyIcon),
                        checkedIcon: checkboxIcon(CheckboxCheckedIcon),
                        indeterminateIcon: checkboxIcon(CheckboxIndeterminateIcon),
                    },
                    // The design-system Checkbox's colors, whatever `color` a checkbox gets. MUI colors the same
                    // states (`primary.main` checked, `action.disabled` disabled); these rules come later, so they
                    // win. That Checkbox fills an empty box with its background; here the drawing's first rect, the
                    // inside of the box, takes the fill. A checked box's first rect is the box itself: it keeps its
                    // color.
                    styleOverrides: {
                        root: {
                            color: colorSystem.neutral[3],
                            background: 'none',
                            '&:hover': {
                                color: colorSystem.primary[3],
                                background: 'none',
                            },
                            '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                                color: colorSystem.primary[5],
                                '&:hover': {
                                    color: colorSystem.primary[7],
                                },
                            },
                            '&.Mui-disabled': {
                                color: colorSystem.neutral[3],
                            },
                            '&:not(.Mui-checked, .MuiCheckbox-indeterminate) > svg > rect:first-of-type': {
                                fill: colorSystem.neutral.white,
                            },
                            '&:not(.Mui-checked, .MuiCheckbox-indeterminate):hover > svg > rect:first-of-type': {
                                fill: colorSystem.primary[1],
                            },
                            '&.Mui-disabled:not(.Mui-checked, .MuiCheckbox-indeterminate) > svg > rect:first-of-type': {
                                fill: colorSystem.neutral[1],
                            },
                        },
                    },
                },
                MuiListItem: {
                    styleOverrides: {
                        root: {
                            boxSizing: 'border-box',
                            background: colorSystem.neutral.white,
                            '&:hover': {
                                background: colorSystem.neutral[1],
                            },
                        },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: {
                            boxSizing: 'border-box',
                            background: colorSystem.neutral.white,
                            '&:hover': {
                                background: colorSystem.neutral[1],
                            },
                        },
                    },
                },
                MuiChip: {
                    defaultProps: {
                        size: 'small',
                        deleteIcon: createElement(CircleXFilledIcon),
                    },
                    styleOverrides: {
                        root: {
                            borderRadius: 24,
                            backgroundColor: colorSystem.neutral[2],
                            color: colorSystem.neutral[7],
                        },
                        colorPrimary: {
                            backgroundColor: colorSystem.primary[2],
                            color: colorSystem.primary[7],
                        },
                        // MUI sizes the delete icon with `font-size` (16 or 22px), which a Lucide icon ignores.
                        deleteIcon: {
                            width: '1em',
                            height: '1em',
                        },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        stickyHeader: {
                            left: 'unset',
                            top: 0,
                        },
                        root: {
                            padding: '16px',
                            fontWeight: 460,
                        },
                    },
                },
                MuiDivider: {
                    styleOverrides: {
                        root: {
                            borderColor: colorSystem.neutral[2],
                            backgroundColor: colorSystem.neutral[2],
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        input: {
                            paddingTop: `11.75px`,
                            paddingBottom: `11.75px`,
                        },
                        notchedOutline: {
                            borderColor: colorSystem.neutral[3],
                        },
                    },
                },
                MuiSelect: {
                    defaultProps: {
                        variant: 'outlined',
                        IconComponent: ChevronDownIcon,
                    },
                    styleOverrides: {
                        select: {
                            minHeight: 'unset',
                        },
                        // MUI centres the icon with `top: calc(50% - .5em)`, which counts on SvgIcon's 24px font size.
                        // A disabled one takes the disabled text color, as in MuiIconButton.
                        icon: {
                            fontSize: '24px',
                            '&.Mui-disabled': {
                                color: colorSystem.neutral[5],
                            },
                        },
                    },
                },
                // Also draws the icon of a Select with `native`.
                MuiNativeSelect: {
                    defaultProps: {
                        IconComponent: ChevronDownIcon,
                    },
                    styleOverrides: {
                        // As for MuiSelect: MUI centres the icon with `top: calc(50% - .5em)`, and a disabled one takes
                        // the disabled text color.
                        icon: {
                            fontSize: '24px',
                            '&.Mui-disabled': {
                                color: colorSystem.neutral[5],
                            },
                        },
                    },
                },
                MuiAutocomplete: {
                    defaultProps: {
                        popupIcon: createElement(ChevronDownIcon, { size: 24 }),
                        clearIcon: createElement(XIcon, { size: 20 }),
                    },
                    styleOverrides: {
                        popupIndicator: {
                            color: colorSystem.neutral[6],
                        },
                        option: {
                            padding: '8px',
                            margin: '4px 2px',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: colorSystem.neutral[2],
                            },
                            '&.Mui-focused:not([aria-selected="true"])': {
                                backgroundColor: colorSystem.neutral[2],
                            },
                            '&[aria-selected="true"]': {
                                backgroundColor: colorSystem.neutral[1],
                                '&:hover': {
                                    backgroundColor: colorSystem.neutral[3],
                                },
                                '&.Mui-focused': {
                                    backgroundColor: colorSystem.neutral[3],
                                },
                            },
                        },
                        inputRoot: {
                            borderRadius: '4px',
                            padding: '6px',
                        },
                        listbox: {
                            borderRadius: '8px',
                            padding: '2px',
                        },
                    },
                },

                MuiRadio: {
                    defaultProps: {
                        icon: radioIcon(RadioEmptyIcon),
                        checkedIcon: radioIcon(RadioCheckedIcon),
                    },
                    // The design-system Radio's colors, which MUI's own would beat: `primary.main` for a checked radio,
                    // `action.disabled` for a disabled one.
                    styleOverrides: {
                        root: {
                            color: colorSystem.neutral[3],
                            '&.Mui-checked': {
                                color: colorSystem.primary[5],
                            },
                            '&.Mui-disabled': {
                                color: colorSystem.neutral[3],
                            },
                        },
                    },
                },
                MuiTab: {
                    styleOverrides: {
                        root: {
                            padding: 0,
                            minHeight: 36,
                            // MUI draws a tab's ring 6px inside its border, across the label of these unpadded tabs.
                            '&.Mui-focusVisible': {
                                outlineOffset: '-2px',
                            },
                        },
                    },
                },
                MuiTabs: {
                    defaultProps: {
                        slots: {
                            startScrollButtonIcon: TabsStartScrollIcon,
                            endScrollButtonIcon: TabsEndScrollIcon,
                        },
                    },
                    styleOverrides: {
                        root: {
                            minHeight: 'unset',
                        },
                    },
                },
                MuiLink: {
                    styleOverrides: {
                        root: {
                            ...body,
                            color: colorSystem.blue[6],
                            textDecoration: 'none',
                        },
                    },
                },
                MuiAccordion: {
                    styleOverrides: {
                        root: {
                            '&.Mui-expanded': {
                                border: '2px solid rgba(94, 129, 244, 0.4)',
                                boxShadow: '0px 4px 8px rgba(158, 158, 158, 0.15)',
                            },
                        },
                    },
                },
                MuiTableSortLabel: {
                    defaultProps: {
                        IconComponent: TableSortArrowIcon,
                    },
                },
                MuiPaginationItem: {
                    defaultProps: {
                        slots: {
                            first: ChevronFirstIcon,
                            previous: ChevronLeftIcon,
                            next: ChevronRightIcon,
                            last: ChevronLastIcon,
                        },
                    },
                    styleOverrides: {
                        // MUI sizes the icon with `font-size` (18, 20 or 22px), which a Lucide icon ignores.
                        icon: {
                            width: '1em',
                            height: '1em',
                        },
                    },
                },
                MuiRating: {
                    defaultProps: {
                        // As MUI's stars: 1em of the rating's font size, which is 18, 24 or 30px by its size.
                        icon: createElement(StarIcon, { size: '1em', fill: 'currentColor' }),
                        emptyIcon: createElement(StarIcon, { size: '1em' }),
                    },
                },
                MuiStepLabel: {
                    defaultProps: {
                        slots: {
                            stepIcon: StepLabelIcon,
                        },
                    },
                },
                MuiSpeedDialIcon: {
                    defaultProps: {
                        icon: createElement(PlusIcon),
                    },
                },
                MuiTablePaginationActions: {
                    defaultProps: {
                        slots: {
                            firstButtonIcon: ChevronFirstIcon,
                            previousButtonIcon: ChevronLeftIcon,
                            nextButtonIcon: ChevronRightIcon,
                            lastButtonIcon: ChevronLastIcon,
                        },
                    },
                },
                MuiAlert: {
                    defaultProps: {
                        // At MUI's 22px, without a color: they take the Alert's, which follows its variant.
                        iconMapping: {
                            info: createElement(STATUS_ICONS.info.outline, { size: 22 }),
                            error: createElement(STATUS_ICONS.error.outline, { size: 22 }),
                            warning: createElement(STATUS_ICONS.warning.outline, { size: 22 }),
                            success: createElement(STATUS_ICONS.success.outline, { size: 22 }),
                        },
                        slots: {
                            closeIcon: AlertCloseIcon,
                        },
                    },
                },
                MuiBreadcrumbs: {
                    defaultProps: {
                        slots: {
                            CollapsedIcon: EllipsisIcon,
                        },
                    },
                },
                MuiAvatar: {
                    defaultProps: {
                        slots: {
                            fallback: AvatarFallbackIcon,
                        },
                    },
                },
                MuiDateCalendar: {
                    defaultProps: {
                        slots: {
                            leftArrowIcon: CalendarLeftArrowIcon,
                            rightArrowIcon: CalendarRightArrowIcon,
                            switchViewIcon: ChevronDownIcon,
                        },
                    },
                },
                MuiSimpleTreeView: {
                    defaultProps: {
                        slots: {
                            expandIcon: TreeExpandIcon,
                            collapseIcon: TreeCollapseIcon,
                        },
                    },
                },
                MuiRichTreeView: {
                    defaultProps: {
                        slots: {
                            expandIcon: TreeExpandIcon,
                            collapseIcon: TreeCollapseIcon,
                        },
                    },
                },
            },
        },
        convertToMuiLocale(locale),
    );

function convertToMuiLocale(locale: LanguageEnum) {
    switch (locale) {
        case 'en':
            return enUS;
        case 'de':
            return deDE;
        case 'es':
            return esES;
        case 'fr':
            return frFR;
        case 'it':
            return itIT;
        case 'th':
            return thTH;
        case 'zh':
            return zhCN;
        case 'ja':
            return jaJP;
        case 'hi':
            return hiIN;
        case 'cs':
            return csCZ;
        case 'nl':
            return nlNL;
        case 'hu':
            return huHU;
        case 'pt':
            return ptPT;
        case 'fi':
            return fiFI;
        case 'sv':
            return svSE;
        case 'pl':
            return plPL;
        case 'tr':
            return trTR;
        default:
            return enUS;
    }
}
